import Big from "big.js";
import { ITEM_BILLABLE_TYPE, LOCATION_BILLING_TYPE, systemDefaultValues, TAX_MODE_KEYS, TAX_MODES, } from "../constants/index.js";
import { getBillToAddressLine1, getBillToAddressLine2, getBillToBusinessNumber, getBillToName, } from "./invoice.js";
import dayjs from "dayjs";
/**
 * Formats a numeric value with a currency code.
 *
 * @param {BigSource} value - The numeric amount to format.
 * @param {string} currency - The currency code (e.g., 'USD', 'AUD').
 * @returns {string} The formatted currency string (e.g., 'USD 123.45').
 */
export const formatValueWithCurrency = (value, currency) => {
    try {
        const upperCode = currency.toUpperCase();
        value = new Big(value ?? 0);
        const numericValue = value.toNumber();
        return `${upperCode} ${numericValue.toFixed(2)}`;
    }
    catch (error) {
        console.error("Error formatting value with currency:", error);
        return `${currency.toUpperCase()} 0.00`;
    }
};
/**
 * Calculates the subtotal, tax, and total bill for a given item.
 * Supports standard and metered billing, regional/affiliate pricing, and tax overrides.
 *
 * @param {object} params - The parameters for the bill calculation.
 * @param {Item | null} params.item - The item to be billed. Returns zero if null.
 * @param {number} [params.totalUsage] - Required for metered items. The total usage to bill for.
 * @param {string} [params.billingType] - The billing type (e.g., REGIONAL) to determine which price to use.
 * @param {SpecifiedTax} [params.specifiedTax] - An optional tax rate and mode to override the item's default tax.
 * @returns An object with the calculated subtotal, tax, total, and billed units.
 */
export const getItemBill = ({ item, totalUsage, billingType, specifiedTax, }) => {
    const ZERO_CHARGE = {
        subtotalAmount: new Big(0),
        taxAmount: new Big(0),
        totalAmount: new Big(0),
        billedUnits: new Big(0),
    };
    if (!item) {
        return ZERO_CHARGE;
    }
    const isMetered = item.billableType === ITEM_BILLABLE_TYPE.METERED_CHARGE;
    if (isMetered && (totalUsage === undefined || totalUsage <= 0)) {
        return ZERO_CHARGE;
    }
    let unitPrice = item.salesPrice;
    if (billingType === LOCATION_BILLING_TYPE.REGIONAL.value &&
        item.hasRegionalVariant) {
        unitPrice = item.regVariantUnitAmount;
    }
    else if (billingType === LOCATION_BILLING_TYPE.AFFILIATE.value &&
        item.enabledAffiliatePricing) {
        unitPrice = item.affiliatePrice;
    }
    let billedUnits = new Big(1);
    let baseCharge = new Big(unitPrice ?? 0);
    if (isMetered) {
        billedUnits =
            item.salesUnitAmount && item.salesUnitAmount > 0
                ? new Big(totalUsage ?? 0)
                    .div(new Big(item.salesUnitAmount))
                    .round(0, Big.roundUp)
                : new Big(0);
        baseCharge = new Big(billedUnits).times(new Big(item.salesPrice ?? 0));
    }
    let taxRate = new Big(item.SalesTaxRate?.total ?? 0).div(new Big(100));
    let taxMode = item.salesTaxMode;
    if (specifiedTax) {
        taxRate = new Big(specifiedTax.taxRate.total ?? 0).div(new Big(100));
        taxMode = specifiedTax.taxMode;
    }
    let subtotalAmount = new Big(0);
    let totalAmount = new Big(0);
    let taxAmount = new Big(0);
    switch (taxMode) {
        case TAX_MODES.INCLUSIVE.id:
            // The base charge is the total amount. We need to extract the tax from it.
            // subtotalAmount = baseCharge / (1 + taxRate)
            subtotalAmount = baseCharge.div(new Big(1).plus(taxRate));
            taxAmount = baseCharge.minus(subtotalAmount);
            totalAmount = baseCharge;
            break;
        case TAX_MODES.EXCLUSIVE.id:
            // The base charge is before tax. We need to add the tax to it.
            // subtotalAmount = baseCharge
            subtotalAmount = baseCharge;
            taxAmount = subtotalAmount.times(taxRate);
            totalAmount = subtotalAmount.plus(taxAmount);
            break;
        case TAX_MODES.NO_TAX.id:
        default:
            // No tax applied.
            subtotalAmount = baseCharge;
            taxAmount = new Big(0);
            totalAmount = baseCharge;
            break;
    }
    return {
        subtotalAmount: subtotalAmount.round(2),
        taxAmount: taxAmount.round(2),
        totalAmount: totalAmount.round(2),
        billedUnits,
    };
};
/**
 * Aggregates tax amounts by tax rate.
 *
 * @param {object} params - The parameters for tax accumulation.
 * @param {TaxTotals} params.taxTotals - The accumulator object for tax totals.
 * @param {Item} params.item - The item providing the tax rate.
 * @param {{ taxAmount: Big.Big }} params.bill - The bill object with the calculated tax amount.
 * @param {SpecifiedTax} [params.specifiedTax] - An optional override for the item's tax rate and mode.
 */
const accumulateTaxTotal = ({ taxTotals, item, bill, specifiedTax, }) => {
    let taxRate = item.SalesTaxRate;
    let taxMode = item.salesTaxMode;
    if (specifiedTax) {
        taxRate = specifiedTax.taxRate;
        taxMode = specifiedTax.taxMode;
    }
    if (!taxRate || taxMode === TAX_MODE_KEYS.NO_TAX) {
        return;
    }
    const taxRateId = taxRate.id;
    if (!taxTotals[taxRateId]) {
        taxTotals[taxRateId] = { taxRate, total: new Big(0) };
    }
    taxTotals[taxRateId].total = taxTotals[taxRateId].total.plus(bill.taxAmount);
};
/**
 * Calculates aggregate billing totals for standard and metered items.
 *
 * This function processes arrays of items to compute a financial summary. It calculates the subtotal,
 * aggregates taxes by rate
 *
 * @param {object} config - The configuration object for the billing summary.
 * @param {Item[]} config.items - Array of standard billable items.
 * @param {object[]} [config.meteredItems] - Array of metered items with their total usage.
 * @param {string} [config.billingType] - The billing type (e.g., 'REGIONAL') to apply specific pricing.
 * @returns {{subtotal: Big, taxTotals: TaxTotals, total: Big}} An object containing the subtotal,
 * tax totals, and the final grand total.
 */
export const getBillingSummary = ({ items, meteredItems, billingType, specifiedTax, }) => {
    let subtotal = new Big(0);
    let total = new Big(0);
    const taxTotals = {};
    if ((!items || items.length === 0) &&
        (!meteredItems || meteredItems.length === 0)) {
        return {
            subtotal: new Big(0),
            taxTotals: {},
            total: new Big(0),
        };
    }
    if (items && items.length > 0) {
        items.forEach((item) => {
            if (!item ||
                !item.isSalesItem ||
                item.billableType !== ITEM_BILLABLE_TYPE.ITEM_CHARGE)
                return;
            const bill = getItemBill({ item, billingType, specifiedTax });
            subtotal = subtotal.plus(bill.subtotalAmount);
            accumulateTaxTotal({ taxTotals, item, bill, specifiedTax });
        });
    }
    if (meteredItems && meteredItems.length > 0) {
        meteredItems?.forEach(({ item, totalUsage }) => {
            if (!item ||
                !item.isSalesItem ||
                item.billableType !== ITEM_BILLABLE_TYPE.METERED_CHARGE)
                return;
            const bill = getItemBill({ item, totalUsage, billingType, specifiedTax });
            subtotal = subtotal.plus(bill.subtotalAmount);
            accumulateTaxTotal({ taxTotals, item, bill, specifiedTax });
        });
    }
    total = subtotal;
    Object.values(taxTotals).forEach((taxTotal) => {
        total = total.plus(taxTotal.total);
    });
    return {
        subtotal: subtotal,
        taxTotals,
        total: total,
    };
};
/**
 * Calculates subtotal, tax amount, and total for a line item.
 *
 * - Handles INCLUSIVE, EXCLUSIVE, and NO_TAX tax modes.
 * - Returns all amounts rounded to two decimal places for financial accuracy.
 *
 * @param price - The unit price of the item.
 * @param quantity - The quantity of the item.
 * @param taxRate - The tax rate as a percentage (e.g., 10 for 10%).
 * @param taxMode - The tax mode ('INCLUSIVE', 'EXCLUSIVE', or 'NO_TAX').
 * @returns An object containing:
 *   - subtotalAmount: The charge before tax.
 *   - taxAmount: The calculated tax amount.
 *   - totalAmount: The final charge including tax.
 */
export const calculateChargeFromValues = ({ price, quantity, taxRateTotal, taxMode, }) => {
    const qty = new Big(quantity);
    const unitPrice = new Big(price);
    const totalPrice = qty.times(unitPrice);
    const taxRateTotalBig = new Big(taxRateTotal ?? 0);
    let taxAmount = new Big(0);
    let subtotalAmount = new Big(0);
    let totalAmount = new Big(0);
    switch (taxMode) {
        case TAX_MODE_KEYS.INCLUSIVE:
            if (taxRateTotalBig.gt(0)) {
                const taxRateValue = taxRateTotalBig.div(new Big(100));
                subtotalAmount = totalPrice.div(new Big(1).plus(taxRateValue));
                taxAmount = totalPrice.minus(subtotalAmount);
                totalAmount = totalPrice;
            }
            else {
                subtotalAmount = totalPrice;
                totalAmount = totalPrice;
            }
            break;
        case TAX_MODE_KEYS.EXCLUSIVE:
            subtotalAmount = totalPrice;
            if (taxRateTotalBig.gt(0)) {
                const taxRateValue = taxRateTotalBig.div(new Big(100));
                taxAmount = subtotalAmount.times(taxRateValue);
                totalAmount = subtotalAmount.plus(taxAmount);
            }
            else {
                totalAmount = subtotalAmount;
            }
            break;
        case TAX_MODES.NO_TAX.id:
        default:
            subtotalAmount = totalPrice;
            totalAmount = totalPrice;
            break;
    }
    return {
        subtotalAmount: subtotalAmount.round(2),
        taxAmount: taxAmount.round(2),
        totalAmount: totalAmount.round(2),
    };
};
/**
 * Splits a fixed total across N lines so the rounded lines sum to that total EXACTLY.
 *
 * Rounding each line on its own does not work: the errors can all fall the same way and
 * push the sum off the total (75/73/175/225 capped at 250 rounds up four times -> 250.01).
 *
 * So we work in whole cents instead. Floor every line first, which makes the sum too low
 * but never too high, then hand the leftover cents to the lines that lost the most in
 * that floor. Handing out exactly the cents we started with is what makes the total exact.
 *
 * Known as the largest remainder (Hamilton) method. Ties go to the earlier line so the
 * same input always produces the same split.
 *
 * @param weights - Relative share per line. Only the ratios matter, not the magnitudes.
 * @param total - The exact amount to distribute, in currency units (e.g. 250 = $250.00).
 * @returns One Big per line, each exact to the cent, summing to `total`.
 */
export const apportionToTotal = (weights, total) => {
    const bigWeights = weights.map((w) => new Big(w));
    const weightSum = bigWeights.reduce((acc, w) => acc.plus(w), new Big(0));
    // No weight to split against - give every line zero rather than dividing by zero.
    if (weightSum.lte(0))
        return bigWeights.map(() => new Big(0));
    const totalCents = new Big(total).times(100).round(0);
    // Floor each line to whole cents and remember the fraction it gave up.
    const lines = bigWeights.map((weight, index) => {
        const exactCents = weight.times(totalCents).div(weightSum);
        const flooredCents = exactCents.round(0, Big.roundDown);
        return {
            index,
            cents: flooredCents,
            remainder: exactCents.minus(flooredCents),
        };
    });
    const distributed = lines.reduce((acc, line) => acc.plus(line.cents), new Big(0));
    const leftover = Number(totalCents.minus(distributed));
    // Give the leftover cents to the biggest losers, one each. Fewer leftovers than lines
    // always (each line gave up under a cent), so no line receives more than one.
    const byRemainder = [...lines].sort((a, b) => b.remainder.cmp(a.remainder) || a.index - b.index);
    for (let i = 0; i < leftover; i++) {
        byRemainder[i].cents = byRemainder[i].cents.plus(1);
    }
    return lines.map((line) => line.cents.div(100));
};
/**
 * Calculates the billing summary for a set of invoice section items.
 *
 * - Iterates through each invoice section item, calculating subtotal and tax amounts.
 * - Handles both INCLUSIVE and EXCLUSIVE tax modes for each item.
 * - Aggregates tax totals by tax name.
 * - Returns rounded values for financial accuracy.
 *
 * @param invoiceSectionItems - Array of InvoiceSectionItem objects to summarize.
 *
 * @returns An object containing:
 *   - subTotal: The sum of gross charges for all items (excluding tax for EXCLUSIVE mode).
 *   - taxTotals: A record mapping tax names to their total tax amounts.
 */
export const getBillingSummaryByInvoiceSectionItems = (invoiceSectionItems) => {
    let subtotal = new Big(0);
    let total = new Big(0);
    const taxTotals = {};
    invoiceSectionItems.forEach((invSecItem) => {
        const { quantity, price, taxRate, taxMode, taxName } = invSecItem;
        const qty = Number(quantity ?? 0);
        const unitPrice = Number(price ?? 0);
        if (qty <= 0 || unitPrice <= 0)
            return;
        const { subtotalAmount, taxAmount, totalAmount } = calculateChargeFromValues({
            price: unitPrice,
            quantity: qty,
            taxRateTotal: taxRate ?? 0,
            taxMode: taxMode ?? TAX_MODE_KEYS.NO_TAX,
        });
        if (taxAmount.gt(0)) {
            const taxKey = taxName || "Tax";
            if (!taxTotals[taxKey]) {
                taxTotals[taxKey] = { total: new Big(0) };
            }
            taxTotals[taxKey].total = taxTotals[taxKey].total.plus(taxAmount);
        }
        subtotal = subtotal.plus(subtotalAmount);
        total = total.plus(totalAmount);
    });
    return {
        subtotal,
        taxTotals,
        total,
    };
};
/**
 * Calculates the total amount refunded for a single payment.
 *
 * This function iterates through all refunds associated with a given payment
 * and sums their amounts. It assumes that the `payment` object includes its `Refunds` array.
 *
 * @param {Payment} payment - The payment object, which should include an array of its associated refunds.
 * @returns {Big} The total refunded amount as a `Big.js` instance. Returns `Big(0)` if there are no refunds.
 */
export function calculatePaymentTotalRefunded(payment) {
    return (payment.Refunds?.reduce((sum, refund) => sum.plus(Big(refund.amount)), Big(0)) ?? new Big(0));
}
/**
 * Calculates the total gross paid and total refunded amounts across a list of payments.
 *
 * This function aggregates the `grossAmount` from all provided payments to get a `totalGrossPaid`.
 * It also sums up the total refunded amount for each payment to get a `totalRefunded`.
 *
 * @param {Payment[]} payments - An array of payment objects. Each payment should have a `grossAmount` and may have an associated `Refunds` array.
 * @returns {{totalGrossPaid: Big, totalRefunded: Big}} An object containing the aggregated total gross paid and total refunded amounts as `Big.js` instances.
 */
export function calculatePaymentsRefundableTotals(payments) {
    let totalGrossPaid = Big(0);
    let totalRefunded = Big(0);
    if (!payments || payments.length === 0) {
        return { totalGrossPaid, totalRefunded };
    }
    payments.forEach((payment) => {
        totalGrossPaid = totalGrossPaid.plus(Big(payment.grossAmount ?? 0));
        const totalRefundedForPayment = calculatePaymentTotalRefunded(payment);
        totalRefunded = totalRefunded.plus(totalRefundedForPayment);
    });
    return { totalGrossPaid, totalRefunded };
}
/**
 * Determines the appropriate tax rate and mode for an item, considering any specified overrides.
 */
export const returnItemProperTaxes = (item, specifiedTax) => {
    let TaxRate = item?.SalesTaxRate;
    let taxName = item?.SalesTaxRate?.name;
    let taxRate = item?.SalesTaxRate?.total;
    let taxMode = item?.salesTaxMode;
    let taxRateId = item?.salesTaxRateId;
    if (specifiedTax) {
        TaxRate = specifiedTax.taxRate;
        taxName = specifiedTax.taxRate.name;
        taxRate = specifiedTax.taxRate.total;
        taxMode = specifiedTax.taxMode;
        taxRateId = specifiedTax.taxRate.id;
    }
    return {
        TaxRate,
        taxName,
        taxRate,
        taxMode,
        taxRateId,
    };
};
/**
 * Filter out invalid items from invoice sections in the invoice editor.
 *
 * - Removes InvoiceSectionItems that do not have a valid associated Item.
 * @param invoiceSections - Array of InvoiceSections to be cleaned.
 * @returns Cleaned array of InvoiceSections with valid InvoiceSectionItems only.
 */
export const cleanInvoiceSectionInvoiceEditor = (invoiceSections) => {
    return invoiceSections.map((section) => ({
        ...section,
        InvoiceSectionItems: section.InvoiceSectionItems?.filter((item) => !!item.itemId),
    }));
};
/**
 * Calculates the billing summary for a credit note.
 *
 * @param creditNote The credit note for which to calculate the billing summary.
 * @returns An object containing the subtotal, taxTotals, and total.
 */
export const creditNoteBillingSummary = (creditNote) => {
    let subtotal = new Big(0);
    let total = new Big(0);
    const taxTotals = {};
    const creditNoteItems = creditNote.CreditNoteItems || [];
    if (creditNoteItems.length === 0) {
        return {
            subtotal: new Big(0),
            taxTotals: {},
            total: new Big(0),
        };
    }
    creditNoteItems.forEach((item) => {
        const { quantity, unitPrice, taxRate, taxMode, taxName } = item;
        const qty = Number(quantity ?? 0);
        const price = Number(unitPrice ?? 0);
        if (qty <= 0 || price <= 0)
            return;
        const { subtotalAmount, taxAmount, totalAmount } = calculateChargeFromValues({
            price,
            quantity: qty,
            taxRateTotal: taxRate ?? 0,
            taxMode: taxMode ?? TAX_MODE_KEYS.NO_TAX,
        });
        if (taxAmount.gt(0)) {
            const taxKey = taxName || "Tax";
            if (!taxTotals[taxKey]) {
                taxTotals[taxKey] = { total: new Big(0) };
            }
            taxTotals[taxKey].total = taxTotals[taxKey].total.plus(taxAmount);
        }
        subtotal = subtotal.plus(subtotalAmount);
        total = total.plus(totalAmount);
    });
    return {
        subtotal,
        taxTotals,
        total,
    };
};
const getCreditNoteIssueDate = (creditNote) => {
    if (creditNote?.issueDateLocal) {
        return dayjs(creditNote.issueDateLocal).format("LL");
    }
    // Fallback to issue date
    if (creditNote?.issueDate) {
        return dayjs(creditNote.issueDate).format("LL");
    }
    return "-";
};
/**
 * Takes raw credit note data and prepares it for rendering in a PDF document.
 */
export const prepareCreditNotePdfData = ({ creditNote, bankDetails, pdfHeaderImgUrl, }) => {
    const { subtotal, taxTotals, total } = creditNoteBillingSummary(creditNote);
    // --- Combine everything ---
    return {
        ...creditNote,
        subtotal,
        taxTotals,
        total,
        issueDate: getCreditNoteIssueDate(creditNote),
        // Assuming billTo details will come from the associated invoice
        billToName: getBillToName(creditNote.Invoice),
        billToBusinessNumber: getBillToBusinessNumber(creditNote.Invoice),
        billToAddressLine1: getBillToAddressLine1(creditNote.Invoice),
        billToAddressLine2: getBillToAddressLine2(creditNote.Invoice),
        // Bank and company details
        bankAccountName: bankDetails?.bankAccountName ?? "",
        bankAccountNumber: bankDetails?.bankAccountNumber ?? "",
        bankAccountBSB: bankDetails?.bankAccountBSB ?? "",
        companyName: bankDetails?.companyName ?? "",
        australianBusinessNumber: bankDetails?.australianBusinessNumber ?? "",
        companyAddress1: bankDetails?.companyAddress1 ?? "",
        companyAddress2: bankDetails?.companyAddress2 ?? "",
        referenceNumberHelpText: bankDetails?.referenceNumberHelpText ?? "",
        remittanceHelpText: bankDetails?.remittanceHelpText ?? "",
        queriesHelpText: bankDetails?.queriesHelpText ?? "",
        phone: bankDetails?.phone ?? "",
        website: bankDetails?.website ?? "",
        companyLogo: pdfHeaderImgUrl || "",
    };
};
/**
 * Get the Respondent's charges for a specific respondent on a batch invoice.
 *
 * @param invoice - The invoice containing the invoice sections and items to calculate charges from.
 * @param respondentId - The ID of the respondent for whom to calculate the charges.
 * @returns
 */
export const getRespondentChargesOnBatchInvoice = (invoice, respondentId) => {
    let subtotalAmount = new Big(0);
    let taxAmount = new Big(0);
    let totalAmount = new Big(0);
    invoice.InvoiceSections?.forEach((section) => {
        if (section.respondentId !== respondentId)
            return;
        section.InvoiceSectionItems?.forEach((item) => {
            const { quantity, price, taxRate, taxMode } = item;
            const qty = Number(quantity ?? 0);
            const unitPrice = Number(price ?? 0);
            if (qty <= 0 || unitPrice <= 0)
                return;
            const { subtotalAmount: chargeSubTotalAmount, taxAmount: chargeTaxAmount, totalAmount: chargeTotalAmount, } = calculateChargeFromValues({
                price: unitPrice,
                quantity: qty,
                taxRateTotal: taxRate ?? 0,
                taxMode: taxMode ?? TAX_MODE_KEYS.NO_TAX,
            });
            subtotalAmount = subtotalAmount.plus(chargeSubTotalAmount);
            taxAmount = taxAmount.plus(chargeTaxAmount);
            totalAmount = totalAmount.plus(chargeTotalAmount);
        });
    });
    return {
        subtotalAmount: subtotalAmount.round(2),
        taxAmount: taxAmount.round(2),
        totalAmount: totalAmount.round(2),
    };
};
export const getSalesItemPrice = (item, billingType) => {
    let price = new Big(item?.salesPrice ?? 0);
    if (billingType === LOCATION_BILLING_TYPE.REGIONAL.value &&
        item?.hasRegionalVariant) {
        price = new Big(item?.regVariantUnitAmount ?? 0);
    }
    else if (billingType === LOCATION_BILLING_TYPE.AFFILIATE.value &&
        item?.enabledAffiliatePricing) {
        price = new Big(item?.affiliatePrice ?? 0);
    }
    return price;
};
// ISO normally uses currency codes in uppercase, so we follow:
// https://www.iso.org/iso-4217-currency-codes.html
export const normalizeCurrencyCode = (currency) => {
    if (!currency) {
        return systemDefaultValues.currency.toUpperCase();
    }
    return currency.toUpperCase();
};
//# sourceMappingURL=salesAndInvoicing.js.map